import React from "react";
import "./GatewayStatsReport.scss";

// The gateway.stats result: summary tiles, network usage, then the devices. The hub has already
// worked out which devices have issues; this only lays them out, problem devices first.

export function formatBytes(bytes) {
	if (bytes < 1024) return bytes + ' B';
	if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
	return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

export function formatDuration(ms) {
	if (ms === null || ms === undefined) return '-';
	const seconds = Math.round(ms / 1000);
	if (seconds < 60) return seconds + 's';
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return minutes + 'm ' + (seconds % 60) + 's';
	return Math.floor(minutes / 60) + 'h ' + (minutes % 60) + 'm';
}

// Measured from when the hub built the report, which is moments before it is shown.
export function formatAgo(time, reportTo) {
	if (!time) return '-';
	const seconds = Math.max(0, Math.round((reportTo - time) / 1000));
	if (seconds < 60) return 'just now';
	return formatDuration(seconds * 1000).split(' ')[0] + ' ago';
}

const formatTime = (time) => time ? new Date(time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '-';

// How each issue reads and how loud it is; an issue from newer firmware still shows, as neutral.
const ISSUES = {
	'silent': {label: 'Silent', tone: 'bad', hint: 'No reading for well over its usual interval'},
	'no-data': {label: 'No data', tone: 'bad', hint: 'No reading today - it may never have connected'},
	'long-gap': {label: 'Long gap', tone: 'warn', hint: 'One gap today far beyond its usual interval'},
	'disconnects': {label: 'Disconnects', tone: 'warn', hint: 'Dropped its connection at least once today'},
};

const compareIds = (a, b) => a.id.localeCompare(b.id, undefined, {numeric: true});

// Each column's sort value; null sorts last whichever way round.
const DEVICE_COLUMNS = [
	{key: 'id', label: 'Asset', value: d => d.id},
	{key: 'issues', label: 'Issues', value: d => d.issues.length},
	{key: 'messages', label: 'Messages', value: d => d.messages, numeric: true},
	{key: 'reports', label: 'Reports', value: d => d.reports, numeric: true},
	{key: 'connects', label: 'Connects', value: d => d.connects, numeric: true},
	{key: 'disconnects', label: 'Disconnects', value: d => d.disconnects, numeric: true},
	{key: 'avgIntervalMs', label: 'Avg interval', value: d => d.avgIntervalMs, numeric: true},
	{key: 'maxIntervalMs', label: 'Max interval', value: d => d.maxIntervalMs, numeric: true},
	{key: 'lastMessageAt', label: 'Last message', value: d => d.lastMessageAt, numeric: true},
];

// Issues first by default, most issues on top, then by asset id.
export function sortDevices(devices, sortKey, descending) {
	const rows = Object.keys(devices || {}).map(id => ({id, ...devices[id], issues: devices[id].issues || []}));
	const column = DEVICE_COLUMNS.find(c => c.key === sortKey) || DEVICE_COLUMNS[1];
	const direction = descending ? -1 : 1;
	return rows.sort(function (a, b) {
		if (column.key === 'id') return direction * compareIds(a, b);
		const va = column.value(a);
		const vb = column.value(b);
		const aMissing = va === null || va === undefined;
		const bMissing = vb === null || vb === undefined;
		if (aMissing || bMissing) return aMissing === bMissing ? compareIds(a, b) : aMissing ? 1 : -1;
		// Ties fall back to ascending asset id, whichever way the column runs
		return va === vb ? compareIds(a, b) : direction * (va < vb ? -1 : 1);
	});
}

function Stat({label, value, detail, tone}) {
	return (
		<div className={'gs-stat' + (tone ? ' ' + tone : '')}>
			<div className={'gs-stat-label'}>{label}</div>
			<div className={'gs-stat-value'}>{value}</div>
			{detail ? <div className={'gs-stat-detail'}>{detail}</div> : null}
		</div>
	);
}

function IssuePill({issue}) {
	const meta = ISSUES[issue] || {label: issue, tone: 'neutral', hint: issue};
	return <span className={'gs-pill ' + meta.tone} title={meta.hint}>{meta.label}</span>;
}

class GatewayStatsReport extends React.Component {

	constructor(props) {
		super(props);
		this.state = {sortKey: 'issues', descending: true, onlyIssues: null};
	}

	sortBy(key) {
		// A new column starts with the biggest figures on top; the asset column starts ascending
		this.setState(state => state.sortKey === key
			? {descending: !state.descending}
			: {sortKey: key, descending: key !== 'id'});
	}

	render() {
		const self = this;
		const report = this.props.report;
		const {messages, socket, uptime, network} = report.gateway;
		const all = sortDevices(report.devices, this.state.sortKey, this.state.descending);
		const flagged = all.filter(d => d.issues.length > 0);
		// Until the viewer picks, a report with issues opens on just those
		const onlyIssues = this.state.onlyIssues === null ? flagged.length > 0 : this.state.onlyIssues;
		const devices = onlyIssues ? flagged : all;
		const readings = all.reduce((n, d) => n + (d.messages || 0), 0);

		const methods = Object.keys(uptime);
		const activeMs = methods.reduce((n, m) => n + uptime[m].activeMs, 0);
		const onlineMs = methods.reduce((n, m) => n + uptime[m].onlineMs, 0);
		const onlinePct = activeMs ? Math.round(onlineMs / activeMs * 1000) / 10 : null;

		const interfaces = Object.keys({...network.today.system, ...network.cumulative.system}).sort();
		const trafficRow = (label, today, total) => (
			<tr key={label}>
				<td>{label}</td>
				<td className={'num'}>{formatBytes((today && today.rxBytes) || 0)}</td>
				<td className={'num'}>{formatBytes((today && today.txBytes) || 0)}</td>
				<td className={'num muted'}>{formatBytes((total && total.rxBytes) || 0)}</td>
				<td className={'num muted'}>{formatBytes((total && total.txBytes) || 0)}</td>
			</tr>
		);

		return (
			<div className={'gateway-stats'}>
				<div className={'gs-header'}>
					<div>
						<div className={'gs-title'}>Today so far</div>
						<div className={'gs-subtitle'}>
							{report.date} (UTC day) · {formatTime(report.from)} to {formatTime(report.to)} your time
						</div>
					</div>
					{all.length === 0 ? null : flagged.length === 0
						? <span className={'gs-pill good'}>All devices healthy</span>
						: <span className={'gs-pill bad'}>{flagged.length} of {all.length} with issues</span>}
				</div>

				<div className={'gs-stats'}>
					<Stat label={'Devices'} value={all.length}
						  detail={flagged.length ? flagged.length + ' with issues' : 'none with issues'}
						  tone={flagged.length ? 'bad' : null}/>
					<Stat label={'Readings'} value={readings.toLocaleString()}
						  detail={messages.sent.toLocaleString() + ' messages sent to the cloud'}/>
					<Stat label={'Spooled / failed'} value={messages.spooled + ' / ' + messages.sendFailed}
						  detail={messages.replayed + ' replayed after reconnecting'}
						  tone={messages.sendFailed ? 'warn' : null}/>
					<Stat label={'Online'} value={onlinePct === null ? '-' : onlinePct + '%'}
						  detail={methods.length
							  ? formatDuration(onlineMs) + ' of ' + formatDuration(activeMs) + ' on ' + methods.join(', ')
							  : 'not measured yet'}
						  tone={onlinePct !== null && onlinePct < 99 ? 'warn' : null}/>
					<Stat label={'Socket connects'} value={socket.connects}
						  detail={socket.connects > 1 ? 'reconnected ' + (socket.connects - 1) + ' times' : 'no reconnects'}
						  tone={socket.connects > 3 ? 'warn' : null}/>
				</div>

				<div className={'gs-section-label'}>Network</div>
				<table className={'gs-table gs-network'}>
					<thead>
						<tr>
							<th/>
							<th className={'num'}>Today rx</th>
							<th className={'num'}>Today tx</th>
							<th className={'num'}>All time rx</th>
							<th className={'num'}>All time tx</th>
						</tr>
					</thead>
					<tbody>
						{trafficRow('app', network.today.app, network.cumulative.app)}
						{interfaces.map(iface => trafficRow(iface, network.today.system[iface], network.cumulative.system[iface]))}
					</tbody>
				</table>

				<div className={'gs-devices-header'}>
					<div className={'gs-section-label'}>Devices</div>
					{all.length ?
						<div className={'gs-segmented'}>
							<button type={'button'} className={onlyIssues ? 'active' : ''}
									onClick={() => self.setState({onlyIssues: true})}>
								With issues ({flagged.length})
							</button>
							<button type={'button'} className={onlyIssues ? '' : 'active'}
									onClick={() => self.setState({onlyIssues: false})}>
								All ({all.length})
							</button>
						</div> : null}
				</div>

				{all.length === 0 ? <div className={'gs-empty'}>No device readings today.</div> :
					devices.length === 0 ? <div className={'gs-empty'}>Every device is reporting normally.</div> :
						<div className={'gs-scroll'}>
							<table className={'gs-table gs-devices'}>
								<thead>
									<tr>
										{DEVICE_COLUMNS.map(c => (
											<th key={c.key} role={'button'} onClick={() => self.sortBy(c.key)}
												className={(c.numeric ? 'num' : '') + (self.state.sortKey === c.key ? ' sorted' : '')}>
												{c.label}
												<span className={'gs-sort'}>
													{self.state.sortKey === c.key ? (self.state.descending ? '▾' : '▴') : ''}
												</span>
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{devices.map(d => (
										<tr key={d.id} className={d.issues.length ? 'gs-issue' : ''}>
											<td className={'gs-asset'}>{d.id}</td>
											<td>
												{d.issues.length
													? d.issues.map(issue => <IssuePill key={issue} issue={issue}/>)
													: <span className={'muted'}>OK</span>}
											</td>
											<td className={'num'}>{d.messages}</td>
											<td className={'num'}>{d.reports}</td>
											<td className={'num'}>{d.connects}</td>
											<td className={'num' + (d.disconnects ? ' warn' : '')}>{d.disconnects}</td>
											<td className={'num'}>{formatDuration(d.avgIntervalMs)}</td>
											<td className={'num'}>{formatDuration(d.maxIntervalMs)}</td>
											<td className={'num'} title={d.lastMessageAt ? new Date(d.lastMessageAt).toLocaleString() : ''}>
												{formatAgo(d.lastMessageAt, report.to)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>}
			</div>
		);
	}
}

export default GatewayStatsReport;
